/**
 * Posta in arrivo dagli ospiti: elenco a sinistra, conversazione a destra.
 *
 * Si apre già filtrata su "da rispondere", perché è la domanda a cui questa
 * schermata serve a rispondere. Categorie e urgenza vengono dal classificatore
 * a parole chiave, che gira senza modello.
 */

import { useEffect, useMemo, useState } from "react";
import {
  AlertTriangle,
  ArrowLeft,
  Inbox as InboxIcon,
  MessageSquare,
} from "lucide-react";
import type { ConversationView, GuestMessageView } from "@otto/shared";
import { api } from "../lib/api.ts";
import { formatWhen } from "../lib/quando.ts";
import { useStore } from "../lib/store.tsx";
import {
  Avatar,
  Badge,
  CountDot,
  EmptyState,
  IconButton,
  Pill,
  Skeleton,
  cx,
} from "../ui/primitives.tsx";
import { DetailPanel, ListPanel, ListRow } from "../shell/ListPanel.tsx";
import { ProposalCard } from "../blocks/ProposalCard.tsx";

const CATEGORIE: Record<string, string> = {
  wifi: "Wifi",
  parcheggio: "Parcheggio",
  check_in: "Check-in",
  check_out: "Check-out",
  bagagli: "Bagagli",
  come_arrivare: "Come arrivare",
  guasto: "Guasto",
  servizi: "Servizi",
  animali: "Animali",
  prezzo_disponibilita: "Prezzo",
  ringraziamento: "Ringraziamento",
  altro: "Altro",
};

type Tab = "da_rispondere" | "tutte";

export function Inbox({
  onAskOtto,
  focusId,
}: {
  onAskOtto: (prompt: string) => void;
  /**
   * La conversazione da aprire subito.
   *
   * Arriva da un avviso: «rispondi all'ospite» sapeva già di quale ospite si
   * trattava, e portava all'elenco intero lasciando all'host il compito di
   * ritrovarlo. Con la conversazione aperta, la scheda si apre già dentro —
   * e se l'avviso riguarda una conversazione archiviata o filtrata, si passa
   * a "Tutte" invece di mostrare una lista in cui non c'è.
   */
  focusId?: string | null;
}) {
  const [tab, setTab] = useState<Tab>(focusId ? "tutte" : "da_rispondere");
  const [all, setAll] = useState<ConversationView[] | null>(null);
  const [query, setQuery] = useState("");
  const [openId, setOpenId] = useState<string | null>(null);
  const [opened, setOpened] = useState(false);

  useEffect(() => {
    void api
      .conversations(false)
      .then(({ conversations }) => setAll(conversations))
      .catch(() => setAll([]));
  }, []);

  const attesa = all?.filter((c) => c.awaitingReply).length ?? 0;

  const filtered = useMemo(() => {
    if (!all) return [];
    const base =
      tab === "da_rispondere" ? all.filter((c) => c.awaitingReply) : all;
    const q = query.trim().toLowerCase();
    if (!q) return base;
    return base.filter((c) =>
      `${c.guestName} ${c.listingNickname ?? ""} ${c.lastMessagePreview}`
        .toLowerCase()
        .includes(q),
    );
  }, [all, tab, query]);

  useEffect(() => {
    if (!openId && filtered.length > 0) setOpenId(filtered[0]!.id);
  }, [filtered, openId]);

  // L'id arrivato dall'avviso vince su tutto, anche se cambia mentre la
  // schermata è già aperta (due avvisi di fila, due ospiti diversi).
  useEffect(() => {
    if (!focusId) return;
    setTab("tutte");
    setOpenId(focusId);
    setOpened(true);
  }, [focusId]);

  return (
    <div className="flex h-full">
      <ListPanel<Tab>
        collapsed={opened}
        tabs={[
          { id: "da_rispondere", label: "Da rispondere", count: attesa },
          { id: "tutte", label: "Tutte", count: all?.length },
        ]}
        tab={tab}
        onTab={setTab}
        query={query}
        onQuery={setQuery}
        searchPlaceholder="Cerca un ospite…"
      >
        {!all ? (
          <div className="flex flex-col gap-2 px-1">
            {Array.from({ length: 6 }, (_, index) => (
              <Skeleton key={index} className="h-[70px]" />
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <EmptyState
            icon={<InboxIcon size={24} />}
            title={
              tab === "da_rispondere"
                ? "Non devi rispondere a nessuno"
                : "Nessuna conversazione"
            }
          />
        ) : (
          filtered.map((conversation) => (
            <ListRow
              key={conversation.id}
              active={conversation.id === openId}
              onClick={() => {
                setOpenId(conversation.id);
                setOpened(true);
              }}
              media={<Avatar name={conversation.guestName} size={44} />}
              title={conversation.guestName}
              meta={formatWhen(conversation.lastMessageAt)}
              preview={conversation.lastMessagePreview || "—"}
              end={conversation.awaitingReply ? <CountDot value={1} /> : null}
            />
          ))
        )}
      </ListPanel>

      <DetailPanel open={opened}>
        <Conversation
          conversationId={openId}
          onAskOtto={onAskOtto}
          onBack={() => setOpened(false)}
        />
      </DetailPanel>
    </div>
  );
}

// ── Conversazione ───────────────────────────────────────────────────────────

function Conversation({
  conversationId,
  onAskOtto,
  onBack,
}: {
  conversationId: string | null;
  onAskOtto: (prompt: string) => void;
  onBack: () => void;
}) {
  const store = useStore();
  const [data, setData] = useState<{
    conversation: ConversationView;
    messages: GuestMessageView[];
  } | null>(null);

  useEffect(() => {
    if (!conversationId) {
      setData(null);
      return;
    }
    setData(null);
    void api
      .conversation(conversationId)
      .then(setData)
      .catch(() => setData(null));
  }, [conversationId]);

  if (!conversationId) {
    return (
      <div className="grid flex-1 place-items-center">
        <EmptyState
          icon={<InboxIcon size={24} />}
          title="Scegli una conversazione"
        />
      </div>
    );
  }

  const draft = [...store.actions.values()].find(
    (action) =>
      action.status === "proposta" &&
      action.kind === "messaggio_ospite" &&
      action.conversationId === data?.conversation.id,
  );

  const ultimoOspite = [...(data?.messages ?? [])]
    .reverse()
    .find((m) => m.direction === "ospite");

  return (
    <>
      <header className="flex items-center gap-3 border-b border-line px-4 py-4 sm:px-6">
        <IconButton
          label="Indietro"
          tone="sunken"
          onClick={onBack}
          className="md:hidden"
        >
          <ArrowLeft size={16} />
        </IconButton>
        {data ? (
          <>
            <div className="relative">
              <Avatar name={data.conversation.guestName} size={44} />
              {data.conversation.awaitingReply ? (
                <span className="absolute -bottom-0.5 -right-0.5 size-3 rounded-full border-2 border-surface bg-accent" />
              ) : null}
            </div>
            <div className="min-w-0 flex-1">
              <p className="truncate text-[16px] font-extrabold leading-tight text-ink">
                {data.conversation.guestName}
              </p>
              <p className="truncate text-[13px] text-ink-soft">
                {data.conversation.listingNickname ?? "Senza immobile"} ·{" "}
                {data.conversation.channel}
              </p>
            </div>
            {data.conversation.awaitingReply ? (
              <Badge tone="warn">in attesa</Badge>
            ) : null}
          </>
        ) : (
          <Skeleton className="h-11 w-56" />
        )}
      </header>

      <div className="scroll-soft flex min-h-0 flex-1 flex-col overflow-y-auto px-4 py-5 sm:px-6">
        <div className="mx-auto mt-auto flex w-full max-w-[720px] flex-col gap-3">
          {!data ? (
            <>
              <Skeleton className="h-16 w-2/3" />
              <Skeleton className="ml-auto h-16 w-1/2" />
            </>
          ) : (
            <>
              {draft ? (
                <div className="mb-1">
                  <p className="mb-2 text-[10px] font-bold uppercase tracking-[0.08em] text-ink-faint">
                    Bozza pronta
                  </p>
                  <ProposalCard actionId={draft.id} fallback={draft.preview} />
                </div>
              ) : null}

              {data.messages.map((message) => (
                <Message key={message.id} message={message} />
              ))}
            </>
          )}
        </div>
      </div>

      {data ? (
        <footer className="border-t border-line px-4 py-4 sm:px-6">
          <div className="mx-auto w-full max-w-[720px]">
            <Pill
              variant="primary"
              block
              icon={<MessageSquare size={15} />}
              onClick={() =>
                onAskOtto(
                  `Rispondi a ${data.conversation.guestName}${
                    ultimoOspite
                      ? ` che ha chiesto: «${ultimoOspite.text.slice(0, 120)}»`
                      : ""
                  }`,
                )
              }
            >
              Fai scrivere la risposta a Otto
            </Pill>
          </div>
        </footer>
      ) : null}
    </>
  );
}

function Message({ message }: { message: GuestMessageView }) {
  const ospite = message.direction === "ospite";

  return (
    <div
      className={cx(
        "animate-rise flex flex-col gap-1",
        ospite ? "items-start" : "items-end",
      )}
    >
      <div
        className={cx(
          "max-w-[80%] px-4 py-2.5 text-[14.5px] leading-relaxed",
          ospite
            ? "rounded-card-lg rounded-bl-md bg-surface-muted text-ink"
            : "rounded-card-lg rounded-br-md bg-accent text-ink-inverse shadow-soft",
        )}
      >
        {message.text}
      </div>

      <div className="flex items-center gap-1.5 px-1">
        {ospite && message.category ? (
          <Badge
            tone={
              message.urgency === "alta"
                ? "danger"
                : message.category === "guasto"
                  ? "warn"
                  : "plain"
            }
          >
            {message.category === "guasto" ? (
              <span className="flex items-center gap-1">
                <AlertTriangle size={10} />
                {CATEGORIE[message.category] ?? message.category}
              </span>
            ) : (
              (CATEGORIE[message.category] ?? message.category)
            )}
          </Badge>
        ) : null}
        <span className="text-[11px] tabular-nums text-ink-faint">
          {formatWhen(message.at)}
        </span>
      </div>
    </div>
  );
}
